import { useCallback, useState, SyntheticEvent } from 'react';
import notify from '../../../shared/feedback/notifications';
import ZentuvoInvoicingApi, { IProcountorInvoicePreviewResponse } from '../ZentuvoInvoicingApi';
import { buildSelectionRequest, getErrorMessage } from '../procountorInvoiceHelpers';

type UseInvoiceActionsArgs = {
    fromDate: Date | null;
    toDate: Date | null;
    directInvoices: boolean;
    selectedRowIds: number[];
    loadPreview: (preserveSelection: boolean) => Promise<IProcountorInvoicePreviewResponse | null>;
};

type UseInvoiceActionsResult = {
    actionInProgress: boolean;
    runStatusUpdate: (orderRowIds: number[], invoiceStatusValue: number, successMessage: string) => void;
    handleExportToCSV: (event: SyntheticEvent) => void;
    handleRollbackExportedInvoices: (event: SyntheticEvent) => void;
    handleMarkInvoicesAsCompleted: (event: SyntheticEvent) => void;
};

export function useInvoiceActions({
    fromDate,
    toDate,
    directInvoices,
    selectedRowIds,
    loadPreview
}: UseInvoiceActionsArgs): UseInvoiceActionsResult {
    const [actionInProgress, setActionInProgress] = useState(false);

    const runStatusUpdate = useCallback(
        (orderRowIds: number[], invoiceStatusValue: number, successMessage: string) => {
            if (!fromDate || !toDate || orderRowIds.length === 0) {
                return;
            }

            setActionInProgress(true);
            ZentuvoInvoicingApi.updateStatus(buildSelectionRequest(fromDate, toDate, directInvoices, orderRowIds), invoiceStatusValue)
                .then(() => {
                    notify.success(successMessage);
                    return loadPreview(true);
                })
                .catch((error) => {
                    notify.failure(getErrorMessage(error, 'Invoice status update failed'));
                    console.error('updateStatus error', error);
                })
                .finally(() => {
                    setActionInProgress(false);
                });
        },
        [fromDate, toDate, directInvoices, loadPreview]
    );

    const handleExportToCSV = useCallback(
        (event: SyntheticEvent) => {
            event.preventDefault();

            if (!fromDate || !toDate || selectedRowIds.length === 0) {
                return;
            }

            setActionInProgress(true);
            ZentuvoInvoicingApi.exportSelectedToCSV(buildSelectionRequest(fromDate, toDate, directInvoices, selectedRowIds))
                .then(() => {
                    notify.success('CSV exported and selected invoices marked as Exported');
                    return loadPreview(true);
                })
                .catch((error) => {
                    notify.failure(getErrorMessage(error, 'CSV export failed'));
                    console.error('exportSelectedToCSV error', error);
                })
                .finally(() => {
                    setActionInProgress(false);
                });
        },
        [fromDate, toDate, directInvoices, selectedRowIds, loadPreview]
    );

    const handleRollbackExportedInvoices = useCallback(
        (event: SyntheticEvent) => {
            event.preventDefault();
            runStatusUpdate(selectedRowIds, 1, 'Selected invoices marked as Not Yet Exported');
        },
        [runStatusUpdate, selectedRowIds]
    );

    const handleMarkInvoicesAsCompleted = useCallback(
        (event: SyntheticEvent) => {
            event.preventDefault();
            runStatusUpdate(selectedRowIds, 2, 'Selected invoices marked as Completed');
        },
        [runStatusUpdate, selectedRowIds]
    );

    return {
        actionInProgress,
        runStatusUpdate,
        handleExportToCSV,
        handleRollbackExportedInvoices,
        handleMarkInvoicesAsCompleted
    };
}
